import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { makePermissionsComplete } from 'server/helpers/permissions/permissions';
import { Users } from 'server/shared/collections/users';
import { getUsersMongoSearchQuery } from 'server/shared/helpers/getUsersMongoSearchQuery';
import { Customer } from 'server/shared/models/customer';
import { CustomerUsersTabsEnum } from 'server/shared/models/customerUsersTabs.enum';
import { CustomerUserStatusEnum } from 'server/shared/models/customerUserStatusEnum';
import { Department } from 'server/shared/models/department';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser, User } from 'server/shared/models/user';
import { UserService } from 'src/app/services/user.service';
import { isEnumValue } from 'src/helpers/filterEnumValue';
import { rxMapToTracker } from 'src/helpers/meteorObservable/observeTracker';
import { queryParam$ } from 'src/helpers/queryParam$';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxDistinctStringArrayChanged as rxDistinctStringArray } from 'src/helpers/rxDistinctStringArray';
import { rxNgZoneRun } from 'src/helpers/rxNgZoneRun';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { SubscriptionService } from '../../shared/subscription.service';

const isEqual = require('lodash/isEqual');

export type UserCounts = {
  [key in RoleEnum]: number;
};

@Component({
  selector: 'customer-users',
  templateUrl: 'customer-users.html',
  styleUrls: ['customer-users.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerUsersComponent implements OnInit, OnDestroy {
  destroyed$ = new Subject<void>();
  loggedInUser?: DispoUser;
  tabsEnum = CustomerUsersTabsEnum;

  searchText$ = new BehaviorSubject<string>('');

  filteredRoles$ = queryParam$(this.route, 'roles').pipe(
    map((rolesParam) => {
      if (!rolesParam) return [];
      const roleKeys = rolesParam.split(',');
      return roleKeys
        .filter((key) => key in RoleEnum && isNaN(Number(key)))
        .map((key) => RoleEnum[key as keyof typeof RoleEnum] as RoleEnum);
    }),
    distinctUntilChanged((a, b) => isEqual(a, b)),
  );
  roleEnum = RoleEnum;

  usersDebounceTimeout?: NodeJS.Timeout;

  selectedDepartmentIds$ = new BehaviorSubject<string[]>([]);

  activeTab$ = queryParam$(this.route, 'tab').pipe(
    map((tab) =>
      tab && isEnumValue(tab, CustomerUsersTabsEnum)
        ? tab
        : CustomerUsersTabsEnum.ACTIVE,
    ),
    distinctUntilChanged(),
  );

  // Convert static properties to observables
  isLoading$ = new BehaviorSubject<boolean>(true);
  loadingCollection$ = new BehaviorSubject<boolean>(false);
  tabsEnum$ = of(CustomerUsersTabsEnum);
  roleEnum$ = of(RoleEnum);
  customer$ = this.customerService.readyDispoCustomer$;

  hasChipSelection$ = combineLatest([
    this.permissionsService.canReadDispoUsers$,
    this.permissionsService.canReadRequesterUsers$,
  ]).pipe(
    map((access) => access.filter(Boolean)),
    // currently anyone can see poolers, so we need to show the chips if we have access to any other role
    map((access) => access.length > 0),
  );
  dispoCustomer$ = this.customerService.readyDispoCustomer$;

  // Convert methods to observables
  stateEnabled$ = this.activeTab$.pipe(
    map(
      (activeTab) => (state: CustomerUsersTabsEnum) =>
        isEqual(activeTab, state),
    ),
  );

  roleFilterEnabled$ = this.filteredRoles$.pipe(
    map((filteredRoles) => (role: RoleEnum) => filteredRoles.includes(role)),
  );

  constructor(
    private dialog: MatDialog,
    private ngZone: NgZone,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    public permissionsService: PermissionsService,
    private customerService: CustomerService,
    private userService: UserService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.initFilters();
    const employeeListPosition = Session.get('employeeListPosition');
    if (employeeListPosition) {
      setTimeout(() => {
        window.scrollTo({
          top: employeeListPosition,
          left: 0,
          behavior: 'smooth',
        });
      }, 100);
    }

    this.isLoading$.next(true);
    this.subscriptionService.usersOfCustomerReady$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.ngZone.run(() => {
          this.isLoading$.next(false);
        });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    Session.set('employeeListPosition', window.scrollY);
  }

  private _getCountsQuery(userIds?: string[]) {
    return { _id: { $in: userIds || [] } };
  }

  requestedUsersCount$ = this.getCount$(
    (customer) => customer.pool.requestedUserIds,
  );

  registeredUsersCount$ = this.getCount$(
    (customer) => customer.pool.registeredUserIds,
  );

  createdUsersCount$ = this.getCount$((customer) => customer.createdUserIds);

  formerUsersCount$ = this.getCount$(
    (customer) => customer.pool.formerUserIds || [],
  );

  getCount$(getIds: (customer: Customer) => string[]): Observable<number> {
    return this.subscriptionService.usersOfCustomerReady$.pipe(
      filter((ready) => ready),
      switchMap(() =>
        this.dispoCustomer$.pipe(
          debounceTime(100),
          map((customer) => getIds(customer)),
          rxDistinctStringArray(),
          switchMap((userIds) => Users.find(this._getCountsQuery(userIds))),
          map((users) => users.length),
          rxNgZoneRun(this.ngZone),
          takeUntil(this.destroyed$),
        ),
      ),
    );
  }

  private initFilters() {
    let queryParams = this.route.snapshot.queryParams;
    if (queryParams.tab) {
      this.openTab(null, queryParams.tab);
    } else {
      this.openTab(null, CustomerUsersTabsEnum.ACTIVE);
    }
  }

  filterRole(event: Event, role: RoleEnum) {
    const currentRoles = this.route.snapshot.queryParams['roles'];
    let selectedRoles: RoleEnum[] = [];

    if (currentRoles) {
      const roleKeys = currentRoles.split(',');
      selectedRoles = roleKeys
        .filter((key: string) => key in RoleEnum && isNaN(Number(key)))
        .map(
          (key: string) => RoleEnum[key as keyof typeof RoleEnum] as RoleEnum,
        );
    }

    if (selectedRoles.includes(role)) {
      selectedRoles = selectedRoles.filter((r) => r !== role);
    } else {
      selectedRoles.push(role);
    }
    const rolesParam =
      selectedRoles.length > 0
        ? selectedRoles.map((r) => RoleEnum[r]).join(',')
        : null;
    this._updateLocation({ roles: rolesParam });
  }

  search(searchText: string): void {
    this.searchText$.next(searchText);
  }

  openAddUserDialog(): void {
    combineLatest([this.userService.readyDispo$, this.customer$])
      .pipe(take(1))
      .subscribe(([user, customer]) => {
        const dialogRef = this.dialog.open(AddUserDialog, {
          data: { user, customer },
        });
        dialogRef
          .afterClosed()
          .subscribe(
            async (result: { email: string; newUserRole: RoleEnum }) => {
              if (result) {
                if (
                  this.permissionsService.canCreateDispoUsers() &&
                  !this.permissionsService.canCreatePoolerUsers() &&
                  (!this.permissionsService.canCreateRequesterUsers() ||
                    !customer?.enableJobRequestProcess)
                ) {
                  result.newUserRole = RoleEnum.DISPO;
                }
                this._addUser(result);
              }
            },
          );
      });
  }

  private _addUser(data: { email: string; newUserRole: RoleEnum }): void {
    // @ts-ignore
    Meteor.apply('addUser', [data.email, data.newUserRole], {
      wait: false,
      onResultReceived: (error, response: any) => {
        if (
          error &&
          'error' in error &&
          (error?.error == 'userAlreadyExistsAsDispo' ||
            error?.error == 'userAlreadyExistsAsPooler')
        ) {
          this.toastService.add({
            type: 'INFO',
            message: error.error,
          });
          return;
        }
        if (error && 'error' in error && error?.error == 'userExistsInDemo') {
          this.toastService.add({
            type: 'ERROR',
            message: error.error,
          });
          return;
        }
        if (error && 'error' in error && error?.error == 'userAlreadyExists') {
          this.toastService.add({
            type: 'ERROR',
            message: error.error,
          });
          return;
        }
        if (error) {
          this.toastService.add({ type: 'ERROR', message: 'serverError' });
          return;
        }
        if (response.status == CustomerUserStatusEnum.REQUESTED) {
          this.toastService.add({ type: 'SUCCESS', message: 'sentInvitation' });
          // redirect to
          this.router
            .navigate(['/profile'], {
              queryParams: {
                userId: response.userId,
              },
            })
            .then();
        }
        if (response.status == CustomerUserStatusEnum.INEXISTENT) {
          this.ngZone.run(() => {
            let queryParams: any = {
              userId: 'new',
              email: data.email,
            };
            if (data.newUserRole === RoleEnum.DISPO) {
              queryParams['isTargetDispo'] = true;
            }
            if (data.newUserRole === RoleEnum.REQUESTER) {
              queryParams['isTargetRequester'] = true;
            }
            this.router
              .navigate(['/profile'], {
                queryParams,
              })
              .then();
          });
        }
        if (response.isInPool) {
          this.toastService.add({ type: 'INFO', message: 'alreadyInPool' });
        }
        if (response.alreadyRequested) {
          this.toastService.add({ type: 'INFO', message: 'alreadyRequested' });
        }
      },
    });
  }

  openTab(event: Event | null, tab: CustomerUsersTabsEnum): void {
    if (event) {
      event.stopPropagation();
    }
    this._updateLocation({ tab: tab });
  }

  private _updateLocation(queryParam: { [key: string]: string | null }) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParam,
          replaceUrl: true,
          queryParamsHandling: 'merge',
        })
        .then();
    });
  }

  selectDepartments(departments: Department[]) {
    this.selectedDepartmentIds$.next([
      ...departments.map((department) => department.id),
    ]);
  }

  getRoleAccess(user: DispoUser): RoleEnum[] {
    const permissions = makePermissionsComplete(user.dispoPermissions);
    const accessRoles: RoleEnum[] = [];
    // pooler read is not enforced, anyone can currently see poolers
    // if (permissions.poolerAccess.read) {
    //   accessRoles.push(RoleEnum.POOL);
    // }
    accessRoles.push(RoleEnum.POOL);
    if (permissions.dispoAccess.read) {
      accessRoles.push(RoleEnum.DISPO);
    }
    if (permissions.requesterAccess.read) {
      accessRoles.push(RoleEnum.REQUESTER);
    }

    return accessRoles;
  }

  getUserIdsForTab(tab: CustomerUsersTabsEnum, customer: Customer): string[] {
    switch (tab) {
      case CustomerUsersTabsEnum.ACTIVE:
        return [
          ...customer.pool.activeUserIds,
          ...customer.dispoIds,
          ...(customer.requesterIds || []),
        ];
      case CustomerUsersTabsEnum.INVITED:
        return customer.pool.requestedUserIds || [];
      case CustomerUsersTabsEnum.REGISTERED:
        return customer.pool.registeredUserIds || [];
      case CustomerUsersTabsEnum.CREATED:
        return customer.createdUserIds || [];
      case CustomerUsersTabsEnum.FORMER:
        return customer.pool.formerUserIds || [];
    }
  }
  departmentQuery$ = combineLatest([
    this.selectedDepartmentIds$,
    this.userService.readyDispo$,
  ]).pipe(
    map(([selectedDepartmentIds, loggedInUser]) => {
      if (!selectedDepartmentIds.length) {
        return undefined;
      }
      let departmentQuery: Record<string, any> = {};
      if (loggedInUser.customerId) {
        departmentQuery = {
          $or: [
            // Poolers in selected departments
            {
              [`customerInformation.${loggedInUser.customerId}.departmentIds`]:
                { $in: selectedDepartmentIds },
            },
            // Dispos with full read access
            {
              [`dispoPermissions.departments.readAll`]: true,
            },
            // Requesters with full read access
            {
              [`departmentAccess.readAll`]: true,
            },
          ],
        };
      }

      selectedDepartmentIds.forEach((departmentId) => {
        departmentQuery['$or'].push({
          [`dispoPermissions.departments.specificDepartmentIds.${departmentId}.read`]:
            true,
        });
        departmentQuery['$or'].push({
          [`departmentAccess.specificDepartmentIds.${departmentId}.read`]: true,
        });
      });
      return departmentQuery;
    }),
  );
  userIdsForTab$ = combineLatest([this.activeTab$, this.dispoCustomer$]).pipe(
    map(([activeTab, customer]) => this.getUserIdsForTab(activeTab, customer)),
    rxDistinctStringArray(),
  );

  allRoleUsersQuery$ = combineLatest([
    this.userService.readyDispo$,
    this.userIdsForTab$,
    this.searchText$,
    this.departmentQuery$,
  ]).pipe(
    map(([dispoUser, userIds, searchText, departmentQuery]) => {
      const $andQuery: Record<string, any>[] = [
        { _id: { $in: userIds } },
        { role: { $in: this.getRoleAccess(dispoUser) } },
        { isIntemp: { $ne: true } },
      ];
      if (departmentQuery) {
        $andQuery.push(departmentQuery);
      }
      if (searchText && searchText != '') {
        $andQuery.push(
          ...getUsersMongoSearchQuery(searchText, dispoUser.customerId),
        );
      }
      return {
        $and: $andQuery,
      };
    }),
    rxDistinctJSONStringify(),
  );

  querySort$ = this.dispoCustomer$.pipe(
    map((customer) =>
      customer.prioritizeEmployeeLastName ? { lastName: 1 } : { firstName: 1 },
    ),
    rxDistinctJSONStringify(),
  );

  allRoleUsers$ = this.subscriptionService.usersOfCustomerReady$.pipe(
    switchMap(() =>
      combineLatest([this.allRoleUsersQuery$, this.querySort$]).pipe(
        rxMapToTracker(([query, sort]) => Users.find(query, { sort }).fetch()),
        rxNgZoneRun(this.ngZone),
        shareReplay(1),
        takeUntil(this.destroyed$),
      ),
    ),
    shareReplay(1),
  );

  userCountsByRole$ = this.allRoleUsers$.pipe(
    map((users) => {
      return users.reduce(
        (acc: UserCounts, user) => {
          acc[user.role]++;
          return acc;
        },
        {
          [RoleEnum.POOL]: 0,
          [RoleEnum.DISPO]: 0,
          [RoleEnum.REQUESTER]: 0,
        },
      );
    }),
    takeUntil(this.destroyed$),
    shareReplay(1),
  );
  poolerUserCounts$ = this.userCountsByRole$.pipe(
    map((counts) => counts[RoleEnum.POOL]),
  );
  dispoUserCounts$ = this.userCountsByRole$.pipe(
    map((counts) => counts[RoleEnum.DISPO]),
  );
  requesterUserCounts$ = this.userCountsByRole$.pipe(
    map((counts) => counts[RoleEnum.REQUESTER]),
  );

  users$ = combineLatest([this.allRoleUsers$, this.filteredRoles$]).pipe(
    map(([users, filteredRoles]) => {
      if (filteredRoles.length) {
        return users.filter((user) => filteredRoles.includes(user.role));
      }
      return users;
    }),
    shareReplay(1),
  );

  usersLimit$ = this.subscriptionService.usersLimit$;

  usersLimited$ = combineLatest([this.users$, this.usersLimit$]).pipe(
    map(([users, usersLimit]) => usersLimit && users.length > usersLimit),
  );

  loadAllUsers() {
    this.subscriptionService.usersLimit$.next(undefined);
  }

  trackUser(index: number, item: User) {
    return item._id;
  }
}

@Component({
  selector: 'add-user-dialog',
  templateUrl: 'add-user-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddUserDialog {
  email$ = new BehaviorSubject<string | undefined>(undefined);
  transformedEmail$ = this.email$.pipe(map((email) => email?.toLowerCase()));
  subscriptions: Subscription = new Subscription();
  newUserRole$ = new BehaviorSubject<RoleEnum>(RoleEnum.POOL);
  toggleValue$ = new BehaviorSubject<boolean>(false);
  roleEnum = RoleEnum;

  @ViewChild('emailInput', { static: true }) emailInput?: NgControl;

  creatableRoles$ = combineLatest([
    this.permissionsService.canCreateDispoUsers$,
    this.permissionsService.canCreatePoolerUsers$,
    this.permissionsService.canCreateRequesterUsers$,
  ]).pipe(
    map(([dispo, pooler, requester]) =>
      [
        dispo ? RoleEnum.DISPO : null,
        pooler ? RoleEnum.POOL : null,
        requester ? RoleEnum.REQUESTER : null,
      ].filter((role): role is RoleEnum => role !== null),
    ),
  );

  canCreateUsers$ = this.creatableRoles$.pipe(map((roles) => roles.length > 0));

  canCreateMultipleRoles$ = this.creatableRoles$.pipe(
    map((roles) => roles.length > 1),
  );

  constructor(
    public dialogRef: MatDialogRef<AddUserDialog>,
    public permissionsService: PermissionsService,
    @Inject(MAT_DIALOG_DATA) public data: { user: User; customer: Customer },
  ) {}

  ngOnInit() {
    this.creatableRoles$.pipe(take(1)).subscribe((roles) => {
      if (roles.length === 1) {
        this.newUserRole$.next(roles[0]);
      }
    });
  }

  toggleUserRole() {
    this.toggleValue$.next(!this.toggleValue$.value);
    this.newUserRole$.next(
      this.toggleValue$.value ? RoleEnum.DISPO : RoleEnum.POOL,
    );
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
